import { useEffect, useRef } from 'react';

// Mirrors INACTIVITY_SESSION_DURATION in api-service.ts: once the tab has been
// hidden at least this long, treat in-memory data fetched before that as stale.
const STALE_AFTER_HIDDEN_MS = 5 * 60 * 1000;

/**
 * Re-runs `refetch` when the tab regains visibility after being hidden for at
 * least `STALE_AFTER_HIDDEN_MS`. Use for any one-shot-on-mount fetch whose data
 * can go stale while the tab sits backgrounded (e.g. left open overnight).
 *
 * `refetch` should perform its own request de-duping/loading-state handling —
 * this hook only decides *when* to call it, not how the fetch itself behaves.
 */
export const useStaleOnResume = (refetch: () => void | Promise<unknown>, enabled: boolean) => {
    const hiddenAtRef = useRef<number | null>(null);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenAtRef.current = Date.now();
                return;
            }

            if (document.visibilityState === 'visible' && hiddenAtRef.current !== null) {
                const hiddenDuration = Date.now() - hiddenAtRef.current;
                hiddenAtRef.current = null;
                if (enabled && hiddenDuration >= STALE_AFTER_HIDDEN_MS) {
                    Promise.resolve(refetch()).catch(err => console.error('useStaleOnResume: refetch failed', err));
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [enabled, refetch]);
};
